import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { PaymentPollingService, TrabajoPago } from '../../../services/pagos/payment-polling.service';

/**
 * Componente del panel flotante de monitoreo de progreso de pagos.
 *
 * Muestra una lista de trabajos de pago en procesamiento asíncrono,
 * con barras de progreso, indicadores de estado y controles para
 * minimizar/expandir y descartar trabajos terminados.
 *
 * Se posiciona en la esquina inferior derecha de la pantalla de pagos
 * a proveedor sin afectar el layout existente.
 */
@Component({
  selector: 'app-payment-progress-panel',
  templateUrl: './payment-progress-panel.component.html',
  styleUrls: ['./payment-progress-panel.component.css']
})
export class PaymentProgressPanelComponent implements OnInit, OnDestroy {

  /** Indica si el panel está minimizado (solo muestra cabecera) */
  minimizado: boolean = false;

  /** Indica si el panel es visible (hay al menos un trabajo no descartado) */
  visible: boolean = false;

  /** Lista de trabajos filtrados (sin descartados) para mostrar en el template */
  trabajosVisibles: TrabajoPago[] = [];

  /** Conteo de trabajos activos (pending + processing) no descartados */
  conteoActivos: number = 0;

  /** Conteo de trabajos terminales (completed + failed) no descartados */
  conteoTerminales: number = 0;

  /** Suscripción al observable de trabajos del servicio de polling */
  private jobsSubscription: Subscription;

  constructor(private paymentPollingService: PaymentPollingService) {}

  /**
   * Inicialización del componente.
   * Se suscribe al observable de trabajos del servicio de polling
   * para actualizar la vista cuando cambia el estado de los trabajos.
   * La lógica completa se implementará en la tarea 3.3.
   */
  ngOnInit(): void {
    // Suscribirse al observable de trabajos del servicio de polling
    this.jobsSubscription = this.paymentPollingService.jobs$.subscribe(
      (trabajos: TrabajoPago[]) => {
        // Filtrar trabajos descartados para la lista visible
        this.trabajosVisibles = trabajos.filter(t => !t.descartado);

        // Calcular visibilidad: visible si hay al menos un trabajo no descartado
        this.visible = this.trabajosVisibles.length > 0;

        // Calcular conteo de trabajos activos (pending o processing)
        this.conteoActivos = this.trabajosVisibles.filter(
          t => t.estado === 'pending' || t.estado === 'processing'
        ).length;

        // Calcular conteo de trabajos terminales (completed o failed)
        this.conteoTerminales = this.trabajosVisibles.filter(
          t => t.estado === 'completed' || t.estado === 'failed'
        ).length;
      }
    );
  }

  /**
   * Destrucción del componente.
   * Cancela la suscripción al observable de trabajos para evitar memory leaks.
   */
  ngOnDestroy(): void {
    // Cancelar suscripción al observable de trabajos
    if (this.jobsSubscription) {
      this.jobsSubscription.unsubscribe();
    }
  }

  /**
   * Alterna el estado minimizado/expandido del panel.
   * Cuando está minimizado, solo se muestra la cabecera con el conteo.
   */
  toggleMinimizado(): void {
    this.minimizado = !this.minimizado;
  }

  /**
   * Descarta un trabajo individual del panel.
   * Delega la operación al servicio de polling que lo marca como descartado
   * y lo remueve de sessionStorage.
   * @param id - ID del trabajo de pago a descartar
   */
  descartarTrabajo(id: number): void {
    this.paymentPollingService.dismissJob(id);
  }

  /**
   * Descarta todos los trabajos en estado terminal (completed o failed).
   * Delega la operación al servicio de polling.
   */
  descartarTodos(): void {
    this.paymentPollingService.dismissAllTerminal();
  }
}
